import { Combobox, Input, Option } from "@fluentui/react-components"
import type { Node } from "@rema/shared/schemas/nodes"
import { useMutation } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { AppDrawerForm } from "../../components/app-drawer-form"
import { FormField } from "../../components/form-field"
import { isApiError } from "../../lib/api"
import { applyFieldErrors, problemMessage } from "../../lib/problem"
import { nodesApi } from "./api"
import { nodesI18n } from "./i18n"

interface ReassignFormValues {
  user: string
  node_id: string
}

export interface ReassignDrawerProps {
  open: boolean
  // Nodos elegibles como destino (no down/disabled)
  nodes: Node[]
  // Preselección opcional del nodo destino (al reasignar desde un nodo concreto)
  defaultNodeId?: string | null
  onClose: () => void
  onReassigned: () => void
}

// Reasignación manual de un usuario a un nodo (F5.5). El usuario se identifica por
// usr_… o por usuario.dominio; el servidor resuelve y persiste la asignación manual.
export const ReassignDrawer = ({
  open,
  nodes,
  defaultNodeId,
  onClose,
  onReassigned,
}: ReassignDrawerProps) => {
  const { control, handleSubmit, setError, reset } = useForm<ReassignFormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { user: "", node_id: defaultNodeId ?? "" },
  })
  // Texto visible del Combobox de nodo (el value del form guarda el id).
  const [nodeText, setNodeText] = useState("")

  useEffect(() => {
    if (!open) return
    reset({ user: "", node_id: defaultNodeId ?? "" })
    const preset = defaultNodeId ? nodes.find((n) => n.id === defaultNodeId) : undefined
    setNodeText(preset ? preset.name : "")
  }, [open, defaultNodeId, nodes, reset])

  const mutation = useMutation({
    mutationFn: (values: ReassignFormValues) =>
      nodesApi.reassign({ user_id: values.user.trim(), node_id: values.node_id }),
    onSuccess: () => onReassigned(),
    onError: (error) => {
      if (isApiError(error)) applyFieldErrors(error.problem, setError)
    },
  })

  // El servidor acepta user_id o identifier en /shard/resolve, pero la reasignación
  // exige user_id; aquí se envía el texto tal cual en user_id si parece un id, y si no,
  // el backend valida y devuelve un error de campo legible.
  const onSubmit = handleSubmit((values) => mutation.mutate(values))

  const formError =
    mutation.isError &&
    isApiError(mutation.error) &&
    (mutation.error.problem.errors?.length ?? 0) === 0
      ? problemMessage(mutation.error.problem)
      : null

  return (
    <AppDrawerForm
      open={open}
      title={nodesI18n.reassign.title}
      onSubmit={onSubmit}
      onClose={onClose}
      submitLabel={nodesI18n.reassign.submit}
      submitting={mutation.isPending}
      error={formError}
    >
      <FormField
        control={control}
        name="user"
        label={nodesI18n.reassign.userLabel}
        required
        hint={nodesI18n.reassign.userHint}
        rules={{ required: nodesI18n.validation.userRequired }}
      >
        {(field, invalid) => (
          <Input
            {...field}
            placeholder={nodesI18n.reassign.userPlaceholder}
            aria-invalid={invalid}
          />
        )}
      </FormField>

      <FormField
        control={control}
        name="node_id"
        label={nodesI18n.reassign.nodeLabel}
        required
        rules={{ required: nodesI18n.validation.nodeRequired }}
      >
        {(field, invalid) => (
          <Combobox
            placeholder={nodesI18n.reassign.nodeLabel}
            selectedOptions={field.value ? [field.value] : []}
            value={nodeText}
            aria-invalid={invalid}
            onOptionSelect={(_, data) => {
              if (!data.optionValue) return
              field.onChange(data.optionValue)
              setNodeText(data.optionText ?? "")
            }}
            onBlur={field.onBlur}
          >
            {nodes.map((node) => (
              <Option key={node.id} value={node.id} text={node.name}>
                {`${node.name} · ${nodesI18n.status[node.status]}`}
              </Option>
            ))}
          </Combobox>
        )}
      </FormField>
    </AppDrawerForm>
  )
}
